import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, MapPin, ChevronDown, Search, Utensils, ShoppingBag, UtensilsCrossed, Home as HomeIcon, LayoutGrid, Receipt } from "lucide-react";
import { QrMedia } from "../public/qr/QrMedia";
import { BkBanner, useBannerColors } from "./BkBanner";
import { darken } from "./bannerColor";
import { ItemCard } from "./ItemCards";
import { SiteFooter } from "./SiteFooter";

/** Shared horizontal scroller: native touch scrolling, desktop arrows, mouse-drag with click suppression. */
const useDragScroll = (ref) => {
  const drag = useRef({ down: false, x: 0, left: 0, moved: false });
  const onPointerDown = (e) => { if (e.pointerType !== "mouse") return; drag.current = { down: true, x: e.clientX, left: ref.current.scrollLeft, moved: false }; };
  const onPointerMove = (e) => { const d = drag.current; if (!d.down) return; const dx = e.clientX - d.x; if (Math.abs(dx) > 6) d.moved = true; ref.current.scrollLeft = d.left - dx; };
  const onPointerUp = () => { drag.current.down = false; setTimeout(() => { drag.current.moved = false; }, 0); };
  const onClickCapture = (e) => { if (drag.current.moved) { e.preventDefault(); e.stopPropagation(); } };
  const by = (dir) => ref.current?.scrollBy({ left: dir * Math.round(ref.current.clientWidth * 0.8), behavior: "smooth" });
  return { handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onPointerLeave: onPointerUp, onClickCapture }, by };
};

const Arrows = ({ by, testId }) => (
  <div className="bk-arrows" aria-hidden="true">
    <button type="button" className="bk-arrow" onClick={() => by(-1)} tabIndex={-1} data-testid={`${testId}-prev`}><ChevronLeft size={18} /></button>
    <button type="button" className="bk-arrow" onClick={() => by(1)} tabIndex={-1} data-testid={`${testId}-next`}><ChevronRight size={18} /></button>
  </div>
);

/** Burger King Style homepage: connected header+banner, two synchronized category rows, scrolling Recommended, Explore, Brand Wall, footer. */
export const HomeBK = ({ slug, site, branch, menu, ctrl, qtyOf, onBanner, sheetsOpen, mode, onMode, onPickBranch }) => {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const brandDark = useMemo(() => darken(site.brand?.primary_color || "#b91c1c", 0.15), [site.brand?.primary_color]);
  const banners = menu.banners || [];
  const colors = useBannerColors(banners, brandDark);
  const [activeColor, setActiveColor] = useState(null);
  const onActiveColor = useCallback((c) => setActiveColor(c), []);
  const bg = darken(activeColor || brandDark, activeColor ? 0.28 : 0);
  const catsRef = useRef(null);
  const recRef = useRef(null);
  const cats = useDragScroll(catsRef);
  const recs = useDragScroll(recRef);
  const items = menu.items || [];
  const categories = menu.categories || [];
  const recommended = (menu.recommended_item_ids || []).map((id) => items.find((i) => i.id === id)).filter((i) => i && i.is_available);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const submitSearch = (e) => { e.preventDefault(); const v = q.trim(); nav(v ? `/order/${slug}/menu?q=${encodeURIComponent(v)}` : `/order/${slug}/menu`); };

  return (
    <div className="bk-home" data-testid="home-bk" style={{ "--bk-bg": bg }} data-bg={bg}>
      <header className="bk-header" data-testid="bk-header">
          <div className="bk-header-row">
            {site.brand.logo_url ? <img src={site.brand.logo_url} alt={site.brand.name} className="os-logo" data-testid="brand-logo" /> : <span className="os-logo-mark" aria-hidden="true"><UtensilsCrossed size={20} /></span>}
            <div className="os-brand">
              <h1 data-testid="brand-name">{site.brand.name}</h1>
              <button type="button" onClick={onPickBranch} className="os-branch-btn bk-branch" data-testid="branch-switch"><MapPin size={13} /><span>{branch ? branch.name : "Select outlet"}</span><ChevronDown size={13} /></button>
            </div>
          </div>
          <div className="bk-tools">
            <form className="bk-search" onSubmit={submitSearch} role="search">
              <Search size={16} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search our menu" aria-label="Search our menu" enterKeyHint="search" data-testid="bk-search" />
            </form>
            {branch && (branch.accepts_dine_in || branch.accepts_takeaway) && (
              <div className="bk-modes" data-testid="order-modes">
                {branch.accepts_dine_in && <button type="button" className={mode === "dine_in" ? "active" : ""} onClick={() => onMode("dine_in")} aria-pressed={mode === "dine_in"} data-testid="mode-dine-in"><Utensils size={13} />Dine-in</button>}
                {branch.accepts_takeaway && <button type="button" className={mode === "takeaway" ? "active" : ""} onClick={() => onMode("takeaway")} aria-pressed={mode === "takeaway"} data-testid="mode-takeaway"><ShoppingBag size={13} />Takeaway</button>}
              </div>
            )}
          </div>
      </header>
      <div className="bk-top">
        {banners.length > 0 && <div className="bk-banner" data-testid="home-banner"><BkBanner banners={banners} onOpen={onBanner} paused={sheetsOpen} colors={colors} onActiveColor={onActiveColor} /></div>}
      </div>

      {categories.length > 0 && (
        <section className="os-section bk-section" data-testid="home-our-menu">
          <div className="os-section-head"><h2>Our Menu</h2><Link to={`/order/${slug}/menu`} className="os-link" data-testid="home-view-full-menu">See all <ArrowRight size={14} /></Link></div>
          <div className="bk-scroller-wrap">
            <div ref={catsRef} className="bk-cats" {...cats.handlers} data-testid="bk-category-scroller" aria-label="Menu categories (two rows scroll together)">
              {categories.map((c) => (
                <Link key={c.id} to={`/order/${slug}/menu/${c.id}`} className="bk-cat" draggable={false} data-testid={`home-category-${c.id}`}>
                  <span className="bk-cat-img"><QrMedia src={c.image_url} alt={c.name} size="large" /></span>
                  <span className="bk-cat-name">{c.name}</span>
                </Link>
              ))}
            </div>
            {categories.length > 6 && <Arrows by={cats.by} testId="bk-cats" />}
          </div>
        </section>
      )}

      {recommended.length > 0 && (
        <section className="os-section bk-section" data-testid="home-recommended">
          <div className="os-section-head"><div><h2>Recommended</h2><p>Hand-picked by {branch?.name}</p></div></div>
          <div className="bk-scroller-wrap">
            <div ref={recRef} className="bk-recs" {...recs.handlers} data-testid="bk-recommended-scroller">
              {recommended.map((it) => <div className="bk-rec" key={it.id}><ItemCard it={it} qty={qtyOf[it.id] || 0} ctrl={ctrl} testPrefix="rec-item" /></div>)}
            </div>
            {recommended.length > 2 && <Arrows by={recs.by} testId="bk-recs" />}
          </div>
        </section>
      )}

      <section className="os-section bk-section">
        <Link to={`/order/${slug}/menu`} className="os-cta bk-cta" data-testid="home-explore-menu"><UtensilsCrossed size={18} />Explore Full Menu <ArrowRight size={18} /></Link>
      </section>

      {menu.brand_wall_published && (
        <section className="os-section bk-section" data-testid="home-brand-wall">
          <Link to={`/order/${slug}/wall`} className="os-wall-tile" data-testid="home-brand-wall-link">
            <div><h3>Our Brand Wall</h3><p>The story, process and people behind {branch?.name}</p></div>
            <span className="os-arrow"><ArrowRight size={18} /></span>
          </Link>
        </section>
      )}

      <SiteFooter brand={site.brand} branch={branch} website={menu.website} />
    </div>
  );
};

/** Bottom navigation (Burger King Style only): real routes only — Home, Menu, My Orders. */
export const BottomNav = ({ slug, active }) => (
  <nav className="bk-nav" aria-label="Site navigation" data-testid="bottom-nav">
    <Link to={`/order/${slug}`} className={active === "home" ? "active" : ""} data-testid="nav-home"><HomeIcon size={20} /><span>Home</span></Link>
    <Link to={`/order/${slug}/menu`} className={active === "menu" ? "active" : ""} data-testid="nav-menu"><LayoutGrid size={20} /><span>Menu</span></Link>
    <Link to={`/order/${slug}/account`} className={active === "orders" ? "active" : ""} data-testid="nav-orders"><Receipt size={20} /><span>My Orders</span></Link>
  </nav>
);
